// CONTRACT:C2-RENDER-ENGINE.1.0
/** Pure TRAIL-HISTORY POLICY for the COP comet-tail / contrail — the append/break/prune rules
 *  that decide what a track's position-history buffer may contain, so the render lane can never
 *  draw an "errant flight-path" segment out of a stale buffer.
 *
 *  WHY: the trail buffer is appended ONLY from the render frame writer (writeFrame), which is
 *  driven by the host rAF loop — and a HIDDEN browser tab pauses rAF while the live feed keeps
 *  upserting latest-position-wins. On refocus, ONE giant jump lands on the dense pre-hide
 *  history: if the jump is under the spatial teleport cap (sized for tick-to-tick steps, not
 *  multi-minute gaps) it draws as a single long straight segment styled as the bright/wide HEAD
 *  of the ramp — the errant-flight-path bug. Positions alone cannot detect that gap; TIME can.
 *  So every stored point carries the wall-clock ms it was recorded ({@link TrailPoint}), and the
 *  append policy BREAKS the trail on a time gap exactly like it already breaks on a spatial
 *  teleport: a hidden/throttled-tab gap must never draw as a segment.
 *
 *  The rules (applied by {@link appendTrailPoint}, in place, in this order):
 *    - unchanged position (same lon/lat as the last point) ⇒ SKIP the append (no fat dot);
 *    - BREAK (history reset) before appending when the step exceeds `teleportNm`
 *      (spatial discontinuity — the existing loop-wrap rule) OR when `nowMs − last.t`
 *      exceeds `maxGapMs` (time discontinuity — the hidden-tab rule);
 *    - after appending: PRUNE points older than `maxAgeMs` (old vapor evaporates — the
 *      contrail read) and CAP the buffer at `maxLen` points (memory bound).
 *
 *  PURE + PRESENTATION ONLY (I13): deterministic, no clock reads (the caller supplies nowMs),
 *  no GPU; this bounds trail EXTENT/continuity, never WHAT renders. Tested directly in
 *  packages/render-engine/test/trailPolicy.test.ts. */

import { segmentNm } from "./geometryGuards.ts";

/** One stored trail sample: the [lon, lat, altMetres] position plus the wall-clock ms it was
 *  recorded. The timestamp is what lets the policy break on a TIME gap (rAF paused, feed alive)
 *  and lets the renderer age-fade each segment like dissipating contrail vapor. */
export interface TrailPoint {
  p: [number, number, number];
  t: number;
}

/** Knobs for {@link appendTrailPoint} — all operator-tunable at the call site
 *  (apps/cop render-tuning.json → trailMaxGapS / trailMaxAgeS / trailLen). */
export interface TrailAppendOpts {
  /** BREAK the trail when the time since the last stored point exceeds this (ms). Sized for
   *  "the tab was hidden / rAF throttled" gaps — well above any live feed cadence. */
  maxGapMs: number;
  /** After appending, PRUNE stored points older than this (ms) — the contrail dissipation. */
  maxAgeMs: number;
  /** BREAK the trail when the step from the last stored point exceeds this (nm) — the existing
   *  spatial teleport / loop-wrap rule (see geometryGuards.MAX_RENDER_SEG_NM). */
  teleportNm: number;
  /** Hard cap on stored points per track (memory bound; oldest dropped first). */
  maxLen: number;
}

/** Append a track's new position to its trail history IN PLACE, applying the break / skip /
 *  prune rules documented in the module header. Mutates `hist` (the caller keeps the same array
 *  identity in its per-track map — matching the pre-policy writeFrame semantics). */
export function appendTrailPoint(
  hist: TrailPoint[],
  p: [number, number, number],
  nowMs: number,
  opts: TrailAppendOpts,
): void {
  const last = hist[hist.length - 1];
  if (last) {
    // Unchanged lon/lat ⇒ no new sample (a static track contributes zero segments — and never
    // a "fat dot" of stacked identical points). Altitude-only wiggle intentionally ignored,
    // matching the pre-policy behavior.
    if (last.p[0] === p[0] && last.p[1] === p[1]) return;
    // BREAK-ON-DISCONTINUITY: a spatial teleport (loop-wrap snap-back) OR a TIME gap (hidden /
    // throttled tab — rAF paused while the feed kept moving the track) must NOT leave a trail
    // segment across the jump. Restart the history from the new position so the tail follows
    // the ACTUAL observed path, never an interpolated leap. segmentNm is +Infinity for a
    // non-finite endpoint, so garbage coordinates also land here (fail to "break", never draw).
    if (segmentNm(last.p, p) > opts.teleportNm || nowMs - last.t > opts.maxGapMs) {
      hist.length = 0;
    }
  }
  hist.push({ p, t: nowMs });
  // PRUNE by age — old vapor evaporates (the contrail read) — then CAP by count. Points are
  // appended in nowMs order, so the stale run is always a prefix: find its end, splice once.
  const cutoff = nowMs - opts.maxAgeMs;
  let firstFresh = 0;
  while (firstFresh < hist.length - 1 && hist[firstFresh].t < cutoff) firstFresh++;
  if (firstFresh > 0) hist.splice(0, firstFresh);
  if (hist.length > opts.maxLen) hist.splice(0, hist.length - opts.maxLen);
}
